"use client";

import { cn } from "@workspace/ui/lib/utils";
import { Activity, useEffect, useMemo, useState } from "react";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@workspace/ui/components/carousel";
import { Button } from "@workspace/ui/components/button";
import ChevronLeft from "@workspace/ui/icons/chevron-left";
import ChevronRight from "@workspace/ui/icons/chevron-right";
import { PillFilter, type PillFilterItem } from "@/components/ui/pill-filter";
import { useEmblaHorizontalScrollAnalytics } from "@/components/analytics/use-embla-horizontal-scroll-analytics";
import {
  TestimonialCard,
  type TestimonialCardProps,
} from "@/components/ui/testimonial-card";
import {
  EventNames,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

export interface TestimonialCarouselCard
  extends Omit<TestimonialCardProps, "tags" | "className" | "index" | "idPrefix"> {
  tagKeys: string[];
}

interface TestimonialCarouselProps {
  tags: PillFilterItem[];
  cards: TestimonialCarouselCard[];
  cardsByFilter?: Partial<Record<string, TestimonialCarouselCard[]>>;
  arrows?: React.ReactNode;
  showFilters?: boolean;
  showTags?: boolean;
  initialActiveTag?: string;
  filterRailClassName?: string;
  filterItemClassName?: string;
  showFilterEndSpacer?: boolean;
  /** Parent uses section horizontal padding + max-w-[1440px]; skip inner lg:px-20 on filter row and pl-6 on pill rail. */
  containedLayout?: boolean;
  /**
   * When set, fires USER_REVIEW_CLICKED on the client when the pill filter changes.
   * Use this from Server Components (do not pass function callbacks across the RSC boundary).
   */
  filterAnalyticsSectionType?: string;
  idPrefix?: string;
}

export function TestimonialCarousel({
  tags,
  cards,
  cardsByFilter,
  arrows,
  showFilters = true,
  showTags = true,
  initialActiveTag,
  filterRailClassName,
  filterItemClassName,
  showFilterEndSpacer = true,
  containedLayout = false,
  filterAnalyticsSectionType,
  idPrefix,
}: TestimonialCarouselProps) {
  const loc = (suffix: string) =>
    idPrefix ? `${idPrefix}-${suffix}` : undefined;
  const [activeTag, setActiveTag] = useState(
    initialActiveTag ?? tags[0]?.key ?? "all",
  );
  const [apiMap, setApiMap] = useState<Record<string, CarouselApi | undefined>>(
    {},
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Derived — no useEffect needed (rerender-derived-state-no-effect)
  const activeApi = apiMap[activeTag];

  const cardsByTag = useMemo(() => {
    const result: Record<string, TestimonialCarouselCard[]> = {};
    for (const tag of tags) {
      const configuredCards = cardsByFilter?.[tag.key];
      if (configuredCards) {
        result[tag.key] = configuredCards;
      } else if (tag.key === "all" || tag.key === tags[0]?.key) {
        result[tag.key] = cards;
      } else {
        result[tag.key] = cards.filter((c) => c.tagKeys.includes(tag.key));
      }
    }
    return result;
  }, [tags, cards, cardsByFilter]);

  const tagLabels = useMemo(
    () => Object.fromEntries(tags.map((t) => [t.key, t.label])),
    [tags],
  );

  // Subscribe to Embla scroll events (legitimate external-system subscription)
  useEffect(() => {
    if (!activeApi) return;
    const update = () => {
      setCanScrollPrev(activeApi.canScrollPrev());
      setCanScrollNext(activeApi.canScrollNext());
    };
    update();
    activeApi.on("select", update);
    activeApi.on("reInit", update);
    return () => {
      activeApi.off("select", update);
      activeApi.off("reInit", update);
    };
  }, [activeApi]);

  useEmblaHorizontalScrollAnalytics({
    api: activeApi,
    sectionType: filterAnalyticsSectionType,
  });

  const defaultArrows = (
    <div className="hidden shrink-0 items-center gap-3 lg:flex">
      <Button
        variant="outline"
        size="icon"
        id={loc("nav-prev")}
        data-testid={loc("nav-prev")}
        className="static size-12 translate-y-0 rounded-full border border-base-7 text-base-7 disabled:opacity-30"
        disabled={!canScrollPrev}
        onClick={() => activeApi?.scrollPrev()}
      >
        <ChevronLeft
          className={cn(
            "!h-6 !w-6 stroke-2",
            canScrollPrev ? "text-primary stroke-primary" : "text-grey",
          )}
        />
        <span className="sr-only">Previous slide</span>
      </Button>
      <Button
        variant="outline"
        size="icon"
        id={loc("nav-next")}
        data-testid={loc("nav-next")}
        className="static size-12 translate-y-0 rounded-full border border-primary text-primary disabled:opacity-30"
        disabled={!canScrollNext}
        onClick={() => activeApi?.scrollNext()}
      >
        <ChevronRight
          className={cn(
            "!h-6 !w-6 stroke-2",
            canScrollNext ? "text-primary stroke-primary" : "text-grey",
          )}
        />
        <span className="sr-only">Next slide</span>
      </Button>
    </div>
  );

  const arrowControls = arrows === undefined ? defaultArrows : arrows;
  const showHeaderRow = showFilters || arrowControls != null;

  return (
    <div className="relative">
      {showHeaderRow ? (
        <div
          className={cn(
            showFilters
              ? "mb-10 flex flex-col gap-5 lg:mb-[54px] lg:gap-8"
              : "hidden lg:absolute lg:right-20 lg:top-[-118px] lg:z-10 lg:block",
          )}
        >
          <div
            className={cn(
              "flex items-center",
              showFilters
                ? cn("justify-between", !containedLayout && "lg:px-20")
                : "justify-end",
            )}
          >
            <div
              className={cn(
                "flex min-w-0 flex-1",
                !showFilters && "hidden",
                showFilters &&
                  filterRailClassName?.includes("px-0") &&
                  "overflow-hidden",
              )}
            >
              <div
                id={loc("filter-hscroll")}
                data-testid={loc("filter-hscroll")}
                className={cn(
                  "no-scrollbar flex min-w-0 flex-1 overflow-x-auto overflow-y-hidden",
                  containedLayout ? "pl-0" : "pl-6 lg:pl-0",
                  filterRailClassName,
                )}
              >
                <PillFilter
                  items={tags}
                  activeKey={activeTag}
                  itemIdPrefix={idPrefix ? `${idPrefix}-filter` : undefined}
                  onSelect={(value) => {
                    setActiveTag(value);
                    if (filterAnalyticsSectionType) {
                      sendEvent(
                        EventNames.USER_REVIEW_CLICKED,
                        withSectionType(filterAnalyticsSectionType, {
                          filter_value: value,
                        }),
                      );
                    }
                  }}
                  size="sm"
                  itemClassName={filterItemClassName}
                />
              </div>
            </div>

            <div className={cn(showFilters && "hidden lg:block lg:ml-6")}>
              {arrowControls}
            </div>
          </div>
        </div>
      ) : null}

      {tags.map((tag) => (
        <Activity
          key={tag.key}
          mode={activeTag === tag.key ? "visible" : "hidden"}
        >
          <Carousel
            id={tag.key === activeTag ? loc("hscroll") : undefined}
            data-testid={tag.key === activeTag ? loc("hscroll") : undefined}
            opts={{ align: "start", loop: false, slidesToScroll: 1 }}
            setApi={(api) => {
              setApiMap((prev) => {
                if (prev[tag.key] === api) return prev;
                return { ...prev, [tag.key]: api };
              });
            }}
          >
            <CarouselContent
              viewportClassName={
                containedLayout ? "px-[30px] lg:px-0" : undefined
              }
              className="ml-0 gap-5"
            >
              {(cardsByTag[tag.key] ?? []).map((card, i) => (
                <CarouselItem
                  key={`${card.name}-${i}`}
                  id={tag.key === activeTag ? loc(`slide-${i + 1}`) : undefined}
                  data-testid={
                    tag.key === activeTag ? loc(`slide-${i + 1}`) : undefined
                  }
                  className="shrink-0 grow-0 basis-[min(332px,100%)] pl-0 pr-0 sm:basis-[332px] lg:basis-[412px]"
                >
                  <TestimonialCard
                    image={card.image}
                    imagePosition={card.imagePosition}
                    tags={card.tagKeys.map((t) => tagLabels[t] ?? t)}
                    quote={card.quote}
                    name={card.name}
                    meta={card.meta}
                    overlayVariant={card.overlayVariant}
                    showTags={showTags}
                    index={tag.key === activeTag ? i + 1 : undefined}
                    idPrefix={tag.key === activeTag ? idPrefix : undefined}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </Activity>
      ))}
    </div>
  );
}
